import { Grid } from '@mui/material';
import { ContractChipList } from '../../_components/atoms/ComparisonTable';
import React from 'react';
import { formatDate } from './helper';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';

export interface CohortComparisonLocationProps {
  state: {
    comparison_id?: string;
    triggerEdit?: any;
  };
}

export const COHORT_COMPARISON_TABLE_COLUMNS = [
  { name: 'Comparison Name' },
  { name: 'Cohorts Selected' },
  { name: 'Trends Analyzed' },
  { name: 'Date Range' },
  { name: 'Created By' },
];

export const MENU_OPTIONS = ['View Trend', 'Edit', 'Delete'];

export const formatComparisonTableData = (data: any[]) => {
  const tableData: any[] = [];
  data.map((item: any) => {
    const otherData = [];
    otherData.push(
      <Grid style={{ width: '200px', overflow: 'hidden' }}>
        {item?.cohortDetails &&
          item.cohortDetails.length > 0 &&
          item.cohortDetails.map((cohort: any, index: number) => (
            <ContractChipList
              key={index}
              selectedItems={
                 
                cohort.cohortDetails?.map((detail: any) => {
                  const value =
                    detail?.multipleProperties?.length > 0
                      ? detail.multipleProperties.join(', ')
                      : detail?.property;

                  return `${detail.category} ${detail.condition} ${value}`;
                }) || []
              }
            />
          ))}
      </Grid>,
    );
    otherData.push(
      <Grid style={{ width: '350px', overflow: 'hidden' }}>
        {item?.trendList &&
          item.trendList.length > 0 &&
          item.trendList.map((trend: any, index: number) => (
            <Grid style={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                fontSize="14px"
                fontType="medium"
                textColor={patientAccessTheme.palette.text.highemphasis}
                styleProps={{
                  width: '200px',
                }}
              >
                {trend?.primaryAxisData?.unit} | {trend?.timeGrain} -
              </Typography>
              {trend?.primaryAxisData &&
                trend.primaryAxisData?.metrics.length > 0 && (
                  <ContractChipList
                    key={index}
                    selectedItems={
                       
                      trend.primaryAxisData.metrics.map((metric: any) => {
                        const options =
                          metric?.options && metric.options.length > 0
                            ?  
                              metric.options
                                 
                                .map((opt: any) => opt.name)
                                .join(', ')
                            : '';

                        return options ? `${metric.name} - ${options}` : '';
                      }) || []
                    }
                  />
                )}
            </Grid>
          ))}
      </Grid>,
    );
    otherData.push(
      <Typography
        fontSize="14px"
        fontType="medium"
        textColor={patientAccessTheme.palette.text.highemphasis}
        styleProps={{
          width: '200px',
        }}
      >
        {item?.aggregationParameter === 'paid'
          ? `${formatDate(item?.paidStartDate, 'DD MMM YYYY')} - ${formatDate(
              item?.paidEndDate,
              'DD MMM YYYY',
            )}`
          : `${formatDate(
              item?.incurredStartDate,
              'DD MMM YYYY',
            )} - ${formatDate(item?.incurredEndDate, 'DD MMM YYYY')}`}
      </Typography>,
    );
    otherData.push(
      <Typography
        fontSize="14px"
        fontType="medium"
        textColor={patientAccessTheme.palette.text.highemphasis}
        styleProps={{
          width: '200px',
        }}
      >
        {item?.createdBy ?? '-'}
      </Typography>,
    );
    tableData.push({
      _id: item._id,
      company_name: item?.comparisonName,
      status: 'Ready',
      otherData,
    });
  });
  return tableData;
};

export const mergeCohortTrendData = (results: any[]) => {
  const merged: any = [];
  results.forEach((cohort: any) => {
     
    cohort.data.forEach((trend: any) => {
      merged.push({
        cohortName: cohort.cohortName,
        cohortColor: cohort.cohortColor,
        // chartStyle: cohort.chartStyle,
        ...trend,
      });
    });
  });

  return merged;
};

export const downloadCSV = (csv: string, filename: string) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const buildCombinedCSV = (
  charts: { type: string; dataSource: any }[],
  trendList: any[],
): string => {
  if (!charts.length) {
    return '';
  }

  const escape = (cell: string) =>
    /[",\n\r]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;

  const stripHtml = (raw: string): string =>
    raw
      .replace(/<[^>]*>/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const multiTrend = charts.length > 1;

  // Build one "block" per trend: { labels, columns: { header, values[] }[] }
  const blocks = charts.map((chart, idx) => {
    const ds = chart.dataSource;
    const trendName = trendList[idx]?.trendName || `Trend ${idx + 1}`;
    const prefix = multiTrend ? `${trendName} - ` : '';

    if (ds?.categories && ds?.dataset) {
      const labels: string[] =
        ds.categories[0]?.category?.map((c: any) => stripHtml(c.label ?? '')) ??
        [];

      const columns = ds.dataset.map((series: any) => {
        const rawHeader =
          series.seriesName ||
          series.seriesname ||
          series.parentYAxis ||
          series.parentyaxis ||
          'Value';
        return {
          header: `${prefix}${rawHeader}`,
           
          values: labels.map((_: string, colIdx: number) =>
            String(series.data?.[colIdx]?.value ?? ''),
          ),
        };
      });

      return { labels, columns };
    }

    if (Array.isArray(ds?.data)) {
      const rawHeader = ds.chart?.yAxisName || trendName || 'Value';
      const labels: string[] = ds.data.map((item: any) =>
        stripHtml(item.label ?? ''),
      );
      return {
        labels,
        columns: [
          {
            header: `${prefix}${rawHeader}`,
            values: ds.data.map((item: any) => String(item.value ?? '')),
          },
        ],
      };
    }

    return { labels: [] as string[], columns: [] };
  });

  // Use labels from first block (periods are shared)
  const periods = blocks[0]?.labels ?? [];
  const allColumns = blocks.flatMap((b) => b.columns);

  const headerRow = ['Period', ...allColumns.map((c) => c.header)];
  const dataRows = periods.map((period, rowIdx) => [
    period,
    ...allColumns.map((c) => c.values[rowIdx] ?? ''),
  ]);

  return [headerRow, ...dataRows]
    .map((row) => row.map(escape).join(','))
    .join('\n');
};
